import { Component } from '@theme/component';
import { applyScrollHintMask } from '@theme/scrolling';

const REF_KEY = '__swiperRef';

/** @type {readonly string[]} */
const SCROLL_HINT_EVENTS = ['progress', 'setTranslate', 'resize', 'update', 'lock', 'unlock'];

/**
 * Theme-owned API on `window.swiper` (lowercase). Does not replace `window.Swiper` from lib-swiper.js.
 *
 * @typedef {{ registry: Map<string, unknown> }} HorizonSwiperApi
 */

/** @returns {HorizonSwiperApi | null} */
function getSwiperApi() {
  if (typeof window === 'undefined') return null;
  const w = /** @type {Window & { swiper?: unknown }} */ (window);

  if (!w.swiper || typeof w.swiper !== 'object') {
    w.swiper = { registry: new Map() };
  }

  const api = /** @type {{ registry?: unknown }} */ (w.swiper);
  if (!(api.registry instanceof Map)) {
    api.registry = new Map();
  }

  return /** @type {HorizonSwiperApi} */ (w.swiper);
}

/** @returns {Map<string, unknown> | null} */
function getRegistry() {
  return getSwiperApi()?.registry ?? null;
}

/**
 * Replace `{ "__swiperRef": "<id>" }` leaves with Swiper instances from the registry.
 *
 * @param {unknown} value
 * @param {Map<string, unknown> | null} registry
 * @returns {unknown}
 */
function resolveSwiperRefs(value, registry) {
  if (!registry || value === null || value === undefined) return value;

  if (Array.isArray(value)) {
    return value.map((item) => resolveSwiperRefs(item, registry));
  }

  if (typeof value === 'object') {
    const obj = /** @type {Record<string, unknown>} */ (value);
    const keys = Object.keys(obj);
    if (keys.length === 1 && keys[0] === REF_KEY && typeof obj[REF_KEY] === 'string') {
      const id = obj[REF_KEY];
      const instance = registry.get(/** @type {string} */ (id));
      if (!instance) {
        console.warn('[swiper-slider] Swiper ref not found (init order?):', id);
      }
      return instance;
    }

    const out = /** @type {Record<string, unknown>} */ ({});
    for (const key of keys) {
      out[key] = resolveSwiperRefs(obj[key], registry);
    }
    return out;
  }

  return value;
}

/**
 * @typedef {object} SwiperSliderRefs
 * @property {HTMLElement} swiperRoot - Element with class `swiper` passed to Swiper.
 */

/**
 * Custom element that boots Swiper from a child `<script type="application/json">`.
 *
 * Opt-in edge fade: set `data-scroll-hint` on the host to apply the same progressive
 * `mask-image` as `<scroll-hint>`, driven by `swiper.progress` (transform-based slides).
 *
 * @extends {Component<SwiperSliderRefs>}
 */
class SwiperSlider extends Component {
  requiredRefs = ['swiperRoot'];

  /** @type {any} */
  #swiper;

  /** @type {number | null} */
  #scrollHintRafId = null;

  /** @type {boolean} */
  #scrollHintBound = false;

  connectedCallback() {
    super.connectedCallback();
    queueMicrotask(() => this.#mount());
  }

  updatedCallback() {
    super.updatedCallback();
    this.#unmount();
    queueMicrotask(() => this.#mount());
  }

  disconnectedCallback() {
    this.#unmount();
    super.disconnectedCallback();
  }

  /** @returns {any} */
  #getSwiperConstructor() {
    if (typeof window === 'undefined') return undefined;
    return window.Swiper;
  }

  /**
   * @returns {Record<string, unknown>}
   */
  #readConfig() {
    const script = this.querySelector(':scope > script[type="application/json"]');
    const raw = script?.textContent?.trim();
    if (!raw) return {};

    try {
      return JSON.parse(raw);
    } catch (err) {
      console.error('[swiper-slider] Invalid JSON in config script', err);
      return {};
    }
  }

  #mount() {
    const SwiperCtor = this.#getSwiperConstructor();
    if (!SwiperCtor) {
      console.warn('[swiper-slider] window.Swiper is missing. Load lib-swiper.js before swiper-slider.js.');
      return;
    }

    const { swiperRoot } = this.refs;
    if (!(swiperRoot instanceof HTMLElement)) return;

    let config = this.#readConfig();
    const registry = getRegistry();
    config = /** @type {Record<string, unknown>} */ (resolveSwiperRefs(config, registry));

    try {
      this.#swiper = new SwiperCtor(swiperRoot, config);
    } catch (err) {
      console.error('[swiper-slider] Swiper failed to initialize', err);
      return;
    }

    const id = this.dataset.swiperId;
    if (id && registry) {
      registry.set(id, this.#swiper);
    }

    this.#bindScrollHint();
  }

  #unmount() {
    this.#unbindScrollHint();

    const registry = getRegistry();
    const id = this.dataset.swiperId;

    if (this.#swiper) {
      try {
        this.#swiper.destroy(true, true);
      } catch {
        // Swiper may already be destroyed after morph
      }
      this.#swiper = undefined;
    }

    if (id && registry?.has(id)) {
      registry.delete(id);
    }
  }

  #bindScrollHint() {
    if (!this.hasAttribute('data-scroll-hint') || !this.#swiper) return;

    for (const event of SCROLL_HINT_EVENTS) {
      this.#swiper.on(event, this.#handleScrollHintUpdate);
    }
    this.#scrollHintBound = true;
    this.#updateScrollHint();
  }

  #unbindScrollHint() {
    if (this.#scrollHintRafId !== null) {
      cancelAnimationFrame(this.#scrollHintRafId);
      this.#scrollHintRafId = null;
    }

    if (this.#swiper && this.#scrollHintBound) {
      for (const event of SCROLL_HINT_EVENTS) {
        this.#swiper.off(event, this.#handleScrollHintUpdate);
      }
    }
    this.#scrollHintBound = false;

    const swiperRoot = this.refs?.swiperRoot;
    if (swiperRoot instanceof HTMLElement) {
      swiperRoot.style.maskImage = '';
    }
  }

  #handleScrollHintUpdate = () => {
    if (this.#scrollHintRafId !== null) return;

    this.#scrollHintRafId = requestAnimationFrame(() => {
      this.#scrollHintRafId = null;
      this.#updateScrollHint();
    });
  };

  #updateScrollHint() {
    if (!this.#swiper) return;

    const { swiperRoot } = this.refs;
    if (!(swiperRoot instanceof HTMLElement)) return;

    const axis = this.#swiper.params?.direction === 'vertical' ? 'vertical' : 'horizontal';

    applyScrollHintMask(swiperRoot, this.#swiper.progress, {
      axis,
      hasOverflow: !this.#swiper.isLocked,
    });
  }
}

if (!customElements.get('swiper-slider')) {
  customElements.define('swiper-slider', SwiperSlider);
}
